import { useState } from 'react'
import { useTranslation } from 'react-i18next'
import { Grid } from '@mui/material'
import { ModalLayout, CardAttribute, LinkButton } from '@/components'
import { getPath, getSpeciesWithResearchTypesString } from '@/utils'
import { SoundRecord as SoundRecordType } from '@/types'
import { ObservationSoundRecordDetails } from '@/features'
import { getUseObservationLogsKey, useModal } from '@/hooks'
import { useQueryClient } from '@tanstack/react-query'
import { PageReferrers, ROUTES_PATH } from '@/constants'

export type SoundRecordProps = {
  soundRecord: SoundRecordType
}

export const SoundRecord = ({ soundRecord }: SoundRecordProps): JSX.Element => {
  const { t } = useTranslation('soundRecords')
  const queryClient = useQueryClient()

  const [data, setData] = useState<SoundRecordType>(soundRecord)

  const updateSoundRecord = async (
    id: number,
    updatedFields: Partial<SoundRecordType>
  ) => {
    setData((prevData) => ({
      ...prevData,
      ...updatedFields,
    }))
    const queryKey = getUseObservationLogsKey()

    await queryClient.invalidateQueries(queryKey)
  }

  const { close } = useModal()

  return (
    <ModalLayout
      title={t(`titles.soundRecordDetailTitle`, { id: data.id })}
      cardContentSx={{ maxHeight: '70vh', overflowY: 'auto' }}
    >
      <Grid container={true} rowSpacing={8.25} width={{ md: 925, sm: 'auto' }}>
        <Grid xs={12} item={true} container={true} spacing={4} mb={4}>
          <Grid xs={6} item={true}>
            <CardAttribute
              title={t('sound-record.fieldTitles.projectNumber')}
              value={
                data.observation_log.research_visit_event?.research_area.project
                  .project_number
              }
            />
          </Grid>
          <Grid xs={6} item={true}>
            <CardAttribute
              title={t('sound-record.fieldTitles.projectName')}
              value={
                data.observation_log.research_visit_event?.research_area.project
                  .name
              }
            />
          </Grid>
          <Grid xs={6} item={true}>
            <CardAttribute
              title={t('sound-record.fieldTitles.species')}
              value={getSpeciesWithResearchTypesString(
                data.observation_log.research_visit_event
              )}
            />
          </Grid>
          <Grid xs={6} item={true}>
            <CardAttribute
              title={t('sound-record.fieldTitles.observationType')}
              value={
                <LinkButton
                  url={getPath(ROUTES_PATH.OBSERVATION_LOGS_DETAIL, {
                    id: data.observation_log.id.toString(),
                  })}
                  title={
                    data.observation_log.research_observation_log_type.name
                  }
                  referrer={PageReferrers.SOUND_RECORD}
                  arrowIcon={true}
                  variant="textThin"
                  action={() => close()}
                />
              }
            />
          </Grid>
        </Grid>
        <ObservationSoundRecordDetails
          researchObservationLogType={
            data.observation_log.research_observation_log_type.code
          }
          soundRecord={data}
          updateSoundRecord={updateSoundRecord}
          showFileLink={true}
        />
      </Grid>
    </ModalLayout>
  )
}
